import * as React from "react";

import { DatasheetItem } from "@modules/datasheets/components/datasheet-item";
import { TextElementProps } from "./text-element.controller";

import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";

import { FieldContainer } from "../field-container";
import { normalizeFontFamilyName } from "@modules/datasheets/utils/normalize-font-family-name";

import "./text-element.styles.scss";

export const TextElement: React.FC<TextElementProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const textStyles = React.useMemo(() => {
    const textStyleValue = datasheetElement.original.textstyle;
    
    if (Array.isArray(textStyleValue)) {
      return textStyleValue.filter((s): s is DatasheetComponentTextStyle => 
        s === DatasheetComponentTextStyle.bold || 
        s === DatasheetComponentTextStyle.italic || 
        s === DatasheetComponentTextStyle.underline
      );
    }
    
    if (typeof textStyleValue === 'number' && textStyleValue !== 0 && textStyleValue !== 33) {
      const styles: Array<DatasheetComponentTextStyle> = [];

      if ((textStyleValue & DatasheetComponentTextStyle.bold) === DatasheetComponentTextStyle.bold) {
        styles.push(DatasheetComponentTextStyle.bold);
      }
      if ((textStyleValue & DatasheetComponentTextStyle.italic) === DatasheetComponentTextStyle.italic) {
        styles.push(DatasheetComponentTextStyle.italic);
      }
      if ((textStyleValue & DatasheetComponentTextStyle.underline) === DatasheetComponentTextStyle.underline) {
        styles.push(DatasheetComponentTextStyle.underline);
      }
      
      return styles;
    }
    
    return [];
  }, [datasheetElement.original.textstyle]);

  const getColor = React.useMemo(() => {
    if (datasheetElement.original.hexColor) {
      return datasheetElement.original.hexColor;
    }
    if (datasheetElement.original.fontColor !== undefined && datasheetElement.original.fontColor !== null) {
      const blue = (datasheetElement.original.fontColor & 0xFF);
      const green = ((datasheetElement.original.fontColor >> 8) & 0xFF);
      const red = ((datasheetElement.original.fontColor >> 16) & 0xFF);
      const hex = [
        red.toString(16).padStart(2, '0'),
        green.toString(16).padStart(2, '0'),
        blue.toString(16).padStart(2, '0'),
      ].join('');
      return `#${hex}`;
    }
    return '#000000';
  }, [datasheetElement.original.fontColor, datasheetElement.original.hexColor]);

  const getTextAlign = React.useMemo(() => {
    const align = datasheetElement.original.align ?? DatasheetFieldComponentTextAlign.left;
    switch (align) {
      case DatasheetFieldComponentTextAlign.center:
        return 'center';
      case DatasheetFieldComponentTextAlign.right:
        return 'right';
      case DatasheetFieldComponentTextAlign.left:
      default:
        return 'left';
    }
  }, [datasheetElement.original.align]);

  return (
    <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
        display: "block",
      }}
    >
      <FieldContainer
        className="DatasheetsResultFieldContainer--decorative"
        width={datasheetElement.original.width}
        height={datasheetElement.original.height}
        showBorder={false}
      >
        <div
          className="DatasheetsResultTextElement"
          style={{
            fontSize: datasheetElement.original.fontsize,
            fontFamily: normalizeFontFamilyName(datasheetElement.original.fontname),
            color: getColor,
            fontWeight: textStyles.includes(DatasheetComponentTextStyle.bold)
              ? 700
              : undefined,
            fontStyle: textStyles.includes(DatasheetComponentTextStyle.italic)
              ? "italic"
              : undefined,
            textDecoration: textStyles.includes(DatasheetComponentTextStyle.underline)
              ? "underline"
              : undefined,
            textAlign: getTextAlign,
            lineHeight: '1.2',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >{datasheetElement.original.text}</div>
      </FieldContainer>
    </DatasheetItem>
  );
});
